import { useEffect, useMemo, useState } from "react";
import Form from "../../../Components/Form";
import FullScreenLoading from "src/Components/Loading/FullScreenLoading";
import { useAppDispatch, useAppSelector } from "src/App/hooks";
import {
  onCCOrderFormValuesChange,
  setCCOrderOrderFormValues,
  setCCOrderOrderTableFetchDataFlag,
} from "src/Features/CCOrder/ccOrderSlice";
import {
  addCCOrder,
  getCCOrders,
  getCCOrderCriterionTypes,
  getCCOrderRules,
} from "src/Requests/ccOrder";
import { toast } from "react-toastify";
import {
  CCOrderSendOrderType,
  ICCOrderAddOrderFormValues,
  ICCOrderCriterionTypes,
  ICCOrderLeg,
  ICCOrderRules,
} from "src/Interfaces/ccOrder";
import SahraConnections from "src/Pages/Sahra/SahraConnections";
import { Button, Modal, StaticDropdown, TextInput } from "src/Components";
import {
  IStepOptionDropdownItem,
  StepOptionOrderType,
} from "src/Interfaces/stepInterfaces";
import {
  getStepInstrumentOptionPair,
  getStepInstruments,
} from "src/Requests/stepRequests";
import { packetErrorHandler } from "src/Utils/requestUtils";
import { orderSideOptions } from "src/Pages/DeltaHedgeOrder/AddOrderBox/SingleOrder";
import { getOptionInstruments } from "src/Requests/coveredCall";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import PortfoGroupUserMultiSelectDropdown from "src/Components/Dropdown/Users/PortfoGroupUserMultiSelectDropdown";
import OrderPreviewTable, {

  isCCOrderPreviewRowSendable,
} from "../OrderPreviewTable";
import {
  fallbackCCOrderCriterionTypes,
  getCCOrderCriterionOptions,
  getCCOrderCriterionTypeName,
  getDefaultCCOrderCriterionType,
} from "src/Utils/ccOrderCriterionTypes";
import { getCCOrderVolumeCalculationOptions } from "src/Utils/ccOrderVolumeCalculationTypes";

enum ModalMode {
  Closed,
  SahraOrderTable,
  OrderPreview,
}

const sendOrderTypeOptions = [
  { id: CCOrderSendOrderType.Automatic, name: "\u062E\u0648\u062F\u06A9\u0627\u0631" },
  { id: CCOrderSendOrderType.Credit, name: "\u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
  { id: CCOrderSendOrderType.NonCredit, name: "\u063A\u06CC\u0631 \u0627\u0639\u062A\u0628\u0627\u0631\u06CC" },
];

const SingleOrder = () => {
  const formValues = useAppSelector(
    (store) => store.ccOrder.addOrderFormValues,
  );

  const dispatch = useAppDispatch();

  const [modalMode, setModalMode] = useState<ModalMode>(ModalMode.Closed);

  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRequest, setPreviewRequest] = useState<ICCOrderAddOrderFormValues | null>(null);
  const [previewValues, setPreviewValues] = useState<
    ICCOrderAddOrderFormValues[] | null
  >(null);
  const [rules, setRules] = useState({} as ICCOrderRules);
  const [criterionTypes, setCriterionTypes] =
    useState<ICCOrderCriterionTypes>(fallbackCCOrderCriterionTypes);

  const [instruments, setInstruments] = useState<IStepOptionDropdownItem[]>([]);
  const [options, setOptions] = useState<DropdownItem[]>([]);

  const sendablePreviewValues = useMemo(
    () => (previewValues || []).filter(isCCOrderPreviewRowSendable),
    [previewValues],
  );

  const sendablePreviewAccountIds = useMemo(
    () =>
      sendablePreviewValues
        .map((value) => Number(value.accountId))
        .filter((accountId) => accountId > 0),
    [sendablePreviewValues],
  );

  useEffect(() => {
    getStepInstruments().then(setInstruments).catch(packetErrorHandler);
    getOptionInstruments().then(setOptions).catch(packetErrorHandler);
    getCCOrderRules().then(setRules).catch(packetErrorHandler);
    getCCOrderCriterionTypes()
      .then(setCriterionTypes)
      .catch(packetErrorHandler);
  }, []);

  const criterionOptions = useMemo(
    () =>
      getCCOrderCriterionOptions(
        criterionTypes,
        formValues?.coveredCallOrderType,
      ),
    [criterionTypes, formValues?.coveredCallOrderType],
  );

  useEffect(() => {
    if (!formValues?.coveredCallOrderType) return;

    const isAllowedType = criterionOptions.some(
      (item) => item.id?.toString() === formValues?.criterionType?.toString(),
    );
    if (isAllowedType) return;

    const defaultCriterionType = getDefaultCCOrderCriterionType(
      criterionTypes,
      formValues.coveredCallOrderType,
    );
    if (!defaultCriterionType) return;

    dispatch(
      onCCOrderFormValuesChange({
        name: "criterionType",
        value: defaultCriterionType,
      }),
    );
  }, [
    criterionOptions,
    criterionTypes,
    formValues?.coveredCallOrderType,
    formValues?.criterionType,
  ]);

  useEffect(() => {
    if (
      !formValues?.coveredCallOrderBuyLegs ||
      formValues?.coveredCallOrderBuyLegs.length <= 1 ||
      !formValues?.coveredCallOrderBuyLegs?.[0]?.instrumentId ||
      formValues?.coveredCallPositionStrategyEnum !== "8" ||
      formValues?.coveredCallOrderBuyLegs?.[1]?.instrumentId
    )
      return;

    getStepInstrumentOptionPair(
      formValues.coveredCallOrderBuyLegs[0].instrumentId,
    )
      .then((resp) => {
        buyOrderLegsChangeHandler("instrumentId", resp.id, 1);
      })
      .catch(packetErrorHandler);
  }, [formValues?.coveredCallOrderBuyLegs]);

  const modalCloseHandler = () => {
    setModalMode(ModalMode.Closed);
  };

  const changeHandler = (name: string, value: any) => {
    if (name === "coveredCallOrderType")
      dispatch(
        onCCOrderFormValuesChange({
          name,
          value: value,
          name2: "coveredCallPositionStrategyEnum",
          value2: null,
          name3: "criterionType",
          value3: getDefaultCCOrderCriterionType(criterionTypes, value),
        }),
      );
    else if (name === "volumeCalculationType")
      dispatch(
        onCCOrderFormValuesChange({
          name,
          value,
          name2: "totalVolume",
          value2: undefined,
          name3: "portfoPercent",
          value3: undefined,
        }),
      );
    else if (name === "coveredCallPositionStrategyEnum") {
      const sides: number[] =
        (value && rules.directionsByPairType?.[Number(value || -1)]) || [];
      const res = {
        ...formValues,
        coveredCallOrderBuyLegs: [] as ICCOrderLeg[],
        coveredCallOrderSellLeg: {} as ICCOrderLeg,
        coveredCallPositionStrategyEnum: value,
      } as ICCOrderAddOrderFormValues;

      if (sides.length) {
        res.coveredCallOrderBuyLegs = [{ orderSide: sides[0] } as ICCOrderLeg];
        res.coveredCallOrderSellLeg = {
          orderSide: sides.length === 3 ? sides[2] : sides[1],
        } as ICCOrderLeg;

        if (sides.length === 3)
          res.coveredCallOrderBuyLegs.push({
            orderSide: sides[1],
          } as ICCOrderLeg);

        dispatch(setCCOrderOrderFormValues(res));
      }
    } else dispatch(onCCOrderFormValuesChange({ name, value }));
  };

  const addStepShekarOrderClickHandler = () => {
    const normalizedValues = previewRequest;
    if (!normalizedValues) return;

    const accountIds = previewValues
      ? sendablePreviewAccountIds
      : normalizedValues.accountIds;

    if (!accountIds.length) {
      toast.error("سفارش قابل ارسالی وجود ندارد.");
      return;
    }

    setLoading(true);
    addCCOrder({
      ...normalizedValues,
      accountIds,
      previewWarnings: [],
    })
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          setModalMode(ModalMode.Closed);
          dispatch(setCCOrderOrderTableFetchDataFlag(true));
          dispatch(
            setCCOrderOrderFormValues({
              coveredCallOrderBuyLegs: formValues?.coveredCallOrderBuyLegs,
              coveredCallOrderSellLeg: formValues?.coveredCallOrderSellLeg,
              coveredCallOrderType: formValues?.coveredCallOrderType,
            } as ICCOrderAddOrderFormValues),
          );
        }
      })
      .catch(packetErrorHandler)
      .finally(() => {
        setLoading(false);
      });
  };

  const getNormalizedOrderValues = () => {
    if (!formValues) return null;

    return {
      ...formValues,
      coveredCallOrderBuyLegs: [
        ...formValues.coveredCallOrderBuyLegs.map((v) => ({
          ...v,
          optionOrderType: v?.optionOrderType || StepOptionOrderType.Open,
        })),
      ],
      coveredCallOrderSellLeg: {
        ...formValues.coveredCallOrderSellLeg,
        optionOrderType:
          formValues.coveredCallOrderSellLeg?.optionOrderType ||
          StepOptionOrderType.Open,
      },
      sendOrderType:
        formValues.sendOrderType ?? CCOrderSendOrderType.Automatic,
      coveredCallPositionStrategyEnum:
        formValues.coveredCallPositionStrategyEnum
          ? Number(formValues.coveredCallPositionStrategyEnum)
          : null,
    } as ICCOrderAddOrderFormValues;
  };

  const openPreviewHandler = () => {
    if (previewLoading || loading) return;
    const normalizedValues = getNormalizedOrderValues();
    if (!normalizedValues) return;

    setPreviewLoading(true);
    getCCOrders({
      ...normalizedValues,
      coveredCallOrderLegs: normalizedValues.coveredCallOrderBuyLegs,
      orderType: normalizedValues.coveredCallOrderType,
      marginPercent: normalizedValues.marginInPercent,
      involvedFund: 0,
      instrumentName: [...normalizedValues.coveredCallOrderBuyLegs, normalizedValues.coveredCallOrderSellLeg].map((leg) => instruments.find((item) => item.id === leg.instrumentId)?.name || options.find((item) => item.id === leg.instrumentId)?.name || leg.instrumentId),
    })
      .then((resp) => {
        setPreviewRequest(normalizedValues);
        setPreviewValues(resp);
        setModalMode(ModalMode.OrderPreview);
      })
      .catch(packetErrorHandler)
      .finally(() => {
        setPreviewLoading(false);
      });
  };

  const getSahraUserTable = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections
          initialSearchParam={{
            userIds: previewValues
              ? sendablePreviewAccountIds
              : formValues?.accountIds,
          }}
        />

        {getBtn(addStepShekarOrderClickHandler, loading)}
      </div>
    );
  };

  const getOrderPreviewTable = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-10">
        <OrderPreviewTable
          values={previewValues}
          coveredCallOrderType={formValues?.coveredCallOrderType!}
        />
        {getBtn(
          () => setModalMode(ModalMode.SahraOrderTable),
          !sendablePreviewValues.length,
        )}
      </div>
    );
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.SahraOrderTable:
        return getSahraUserTable();

      case ModalMode.OrderPreview:
        return getOrderPreviewTable();
    }
  };

  const getBtn = (onClick: () => void, disabled?: boolean, isLoading = false) => (
    <Button
      text="ارسال"
      onClick={onClick}
      variant="contained"
      disabled={disabled}
      loading={isLoading}
    />
  );

  const checkBtnDisabled = () => {
    if (
      !formValues?.accountIds ||
      !formValues?.accountIds.length ||
      !formValues?.blockSize?.toString() ||
      !formValues?.criterion?.toString() ||
      !formValues?.coveredCallOrderBuyLegs ||
      !formValues?.coveredCallOrderBuyLegs?.length ||
      !formValues?.marginInPercent?.toString() ||
      !formValues?.coveredCallOrderSellLeg ||
      !formValues?.coveredCallOrderSellLeg?.instrumentId ||
      (!formValues?.totalVolume?.toString() &&
        !formValues?.portfoPercent?.toString())
    )
      return true;

    return false;
  };

  const buyOrderLegsChangeHandler = (
    name: string,
    value: any,
    index: number,
  ) => {
    const clone: ICCOrderLeg[] = [
      ...(formValues?.coveredCallOrderBuyLegs || []),
    ];

    clone.splice(index, 1, {
      ...(clone[index] ? clone[index] : ({} as ICCOrderLeg)),
      [name]: value,
    });
    dispatch(
      setCCOrderOrderFormValues({
        ...formValues,
        coveredCallOrderBuyLegs: clone,
      } as ICCOrderAddOrderFormValues),
    );
  };

  const sellOrderLegsChangeHandler = (name: string, value: any) => {
    dispatch(
      setCCOrderOrderFormValues({
        ...formValues,
        coveredCallOrderSellLeg: {
          ...(formValues?.coveredCallOrderSellLeg || ({} as ICCOrderLeg)),
          [name]: value,
        },
      } as ICCOrderAddOrderFormValues),
    );
  };

  const volumeCalculationOptions = useMemo(
    () =>
      getCCOrderVolumeCalculationOptions(
        rules.volumeCalculationTypesByProcess?.[
          Number(formValues?.coveredCallOrderType || -1)
        ],
      ),
    [
      formValues?.coveredCallOrderType,
      rules.volumeCalculationTypesByProcess,
    ],
  );

  const volume = useMemo(
    () =>
      volumeCalculationOptions.find(
        (item) => item.id === formValues?.volumeCalculationType,
      )?.name ?? "حجم",
    [formValues?.volumeCalculationType, volumeCalculationOptions],
  );

  return (
    <Form className="!w-80 gap-3 p-2 px-2">
      {previewLoading ? <FullScreenLoading /> : <></>}
      {modalMode !== ModalMode.Closed ? (
        <Modal title="ثبت سفارش" modalBoxClassName="!min-w-0 w-[1320px]" onClose={modalCloseHandler} UI={getModalUI()} />
      ) : (
        <></>
      )}

      <PortfoGroupUserMultiSelectDropdown
        name="accountIds"
        label="نام اکانت ها"
        value={formValues?.accountIds}
        onChange={changeHandler}
        withTagBox
        extraLabel
      />
      <div className="flex flex-row justify-start items-center gap-1 w-full">
        <div className="w-40">
          <StaticDropdown
            dropdown={{
              name: "coveredCallOrderType",
              placeholder: "فرایند",
              extraLabel: true,
            }}
            onChange={changeHandler}
            options={rules.processeDropdowns}
            value={formValues?.coveredCallOrderType}
          />
        </div>

        <StaticDropdown
          dropdown={{
            name: "coveredCallPositionStrategyEnum",
            placeholder: "نوع جفت",
            extraLabel: true,
          }}
          value={formValues?.coveredCallPositionStrategyEnum}
          onChange={changeHandler}
          options={
            rules.pairTypeByProcessDropdown?.[
              Number(formValues?.coveredCallOrderType || -1)
            ]
          }
        />
      </div>

      <div className="flex flex-row justify-start items-center gap-1 w-full">
        <StaticDropdown
          dropdown={{
            name: "orderSide",
            placeholder: "جهت 1",
            extraLabel: true,
            disabled: true,
          }}
          onChange={() => {}}
          options={orderSideOptions}
          value={formValues?.coveredCallOrderBuyLegs[0]?.orderSide}
        />
        <StaticDropdown
          dropdown={{
            name: "orderSide",
            placeholder: "جهت 2",
            extraLabel: true,
            disabled: true,
          }}
          onChange={() => {}}
          options={orderSideOptions}
          value={
            formValues?.coveredCallPositionStrategyEnum?.toString() === "8" // کاورد کال مصنوعی
              ? formValues?.coveredCallOrderBuyLegs[1]?.orderSide
              : formValues?.coveredCallOrderSellLeg?.orderSide
          }
        />
        {formValues?.coveredCallPositionStrategyEnum?.toString() === "8" ? ( // کاورد کال مصنوعی
          <StaticDropdown
            dropdown={{
              name: "orderSide",
              placeholder: "جهت 3",
              extraLabel: true,
              disabled: true,
            }}
            onChange={() => {}}
            options={orderSideOptions}
            value={formValues?.coveredCallOrderSellLeg?.orderSide}
          />
        ) : (
          <></>
        )}
      </div>

      <StaticDropdown
        value={formValues?.coveredCallOrderBuyLegs?.[0]?.instrumentId}
        onChange={(name: string, value: any) =>
          buyOrderLegsChangeHandler(name, value, 0)
        }
        dropdown={{
          name: "instrumentId",
          placeholder: `نماد اول`,
          extraLabel: true,
        }}
        options={
          formValues?.coveredCallPositionStrategyEnum?.toString() === "8"
            ? options
            : instruments
        }
      />

      <StaticDropdown
        value={
          formValues?.coveredCallPositionStrategyEnum?.toString() === "8" // کاورد کال مصنوعی
            ? formValues?.coveredCallOrderBuyLegs?.[1].instrumentId
            : formValues?.coveredCallOrderSellLeg?.instrumentId
        }
        onChange={(name: string, value: any) =>
          formValues?.coveredCallPositionStrategyEnum?.toString() === "8"
            ? buyOrderLegsChangeHandler(name, value, 1)
            : sellOrderLegsChangeHandler(name, value)
        }
        dropdown={{
          name: "instrumentId",
          placeholder: `نماد دوم`,
          extraLabel: true,
          disabled:
            formValues?.coveredCallPositionStrategyEnum?.toString() === "8",
        }}
        options={instruments}
      />

      {formValues?.coveredCallPositionStrategyEnum?.toString() === "8" ? ( // کاورد کال مصنوعی
        <StaticDropdown
          value={
            formValues?.coveredCallOrderSellLeg
              ? formValues?.coveredCallOrderSellLeg.instrumentId
              : (null as any)
          }
          onChange={sellOrderLegsChangeHandler}
          dropdown={{
            name: "instrumentId",
            placeholder: `نماد سوم`,
            extraLabel: true,
          }}
          options={instruments}
        />
      ) : (
        <></>
      )}
      <div className="w-full flex flex-row justify-start gap-1">
        <StaticDropdown
          dropdown={{
            name: "volumeCalculationType",
            placeholder: "روش تعیین حجم",
            extraLabel: true,
          }}
          value={formValues?.volumeCalculationType}
          onChange={changeHandler}
          options={volumeCalculationOptions}
        />
        <TextInput
          label={volume}
          name={
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          }
          onChange={changeHandler}
          value={formValues?.[
            formValues?.volumeCalculationType?.toString() === "1"
              ? "totalVolume"
              : "portfoPercent"
          ]?.toString()}
          extraLabel
        />
      </div>
      {criterionOptions.length > 1 ? (
        <StaticDropdown
          dropdown={{
            name: "criterionType",
            placeholder: "نوع معیار",
            extraLabel: true,
          }}
          onChange={changeHandler}
          options={criterionOptions}
          value={formValues?.criterionType}
        />
      ) : (
        <></>
      )}
      <TextInput
        label={`معیار: ${getCCOrderCriterionTypeName(criterionTypes, formValues?.coveredCallOrderType, formValues?.criterionType) || rules.criteriaByProcessDropdown?.[Number(formValues?.coveredCallOrderType || -1)] || ""}`}
        name="criterion"
        onChange={changeHandler}
        value={formValues?.criterion?.toString()}
        classname="ltr text-right"
        extraLabel
      />
      <TextInput
        label="درصد بلوک"
        name="blockSize"
        extraLabel
        onChange={changeHandler}
        value={formValues?.blockSize?.toString()}
        kind="percent"
      />
      <TextInput
        label="درصد مارجین"
        name="marginInPercent"
        onChange={changeHandler}
        value={formValues?.marginInPercent?.toString()}
        kind="percent"
        extraLabel
      />
      <StaticDropdown
        dropdown={{
          name: "sendOrderType",
          placeholder: "\u062A\u0631\u062A\u06CC\u0628 \u0627\u0631\u0633\u0627\u0644",
          extraLabel: true,
        }}
        onChange={changeHandler}
        options={sendOrderTypeOptions}
        value={formValues?.sendOrderType ?? CCOrderSendOrderType.Automatic}
      />
      {getBtn(openPreviewHandler, checkBtnDisabled() || previewLoading || loading, previewLoading)}
    </Form>
  );
};

export default SingleOrder;
